import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { reportQuarterTableStyles } from "../../../styles/ReportQuarterTable.styles";
import { useState, useCallback, useMemo } from "react";
import { QuarterTableEditModal } from "./QuarterTableEditModal";
import { Card } from "@mui/material";
import { ReportQuarterTableColumn, QuarterRow } from "../../../constants";

export const defaultQuarterRows: QuarterRow[] = [
  {
    id: 1,
    [ReportQuarterTableColumn.Period]: "Q1 2024",
    [ReportQuarterTableColumn.StockPrice]: 300.0,
    [ReportQuarterTableColumn.OptionIncome]: 35000,
    [ReportQuarterTableColumn.DividenIncome]: 10000,
    [ReportQuarterTableColumn.CumulativeIncome]: 35000,
    [ReportQuarterTableColumn.Comment]: "Flat quarter; income drove return",
  },
  {
    id: 2,
    [ReportQuarterTableColumn.Period]: "Q2 2024",
    [ReportQuarterTableColumn.StockPrice]: 300.0,
    [ReportQuarterTableColumn.OptionIncome]: 50000,
    [ReportQuarterTableColumn.DividenIncome]: 15000,
    [ReportQuarterTableColumn.CumulativeIncome]: 85000,
    [ReportQuarterTableColumn.Comment]: "Rally; some upside called away",
  },
  {
    id: 3,
    [ReportQuarterTableColumn.Period]: "Q3 2024",
    [ReportQuarterTableColumn.StockPrice]: 300.0,
    [ReportQuarterTableColumn.OptionIncome]: 30000,
    [ReportQuarterTableColumn.DividenIncome]: 20000,
    [ReportQuarterTableColumn.CumulativeIncome]: 115000,
    [ReportQuarterTableColumn.Comment]: "Steady growth; positions rolled",
  },
  {
    id: 4,
    [ReportQuarterTableColumn.Period]: "Q4 2024",
    [ReportQuarterTableColumn.StockPrice]: 300.0,
    [ReportQuarterTableColumn.OptionIncome]: 40000,
    [ReportQuarterTableColumn.DividenIncome]: 25000,
    [ReportQuarterTableColumn.CumulativeIncome]: 155000,
    [ReportQuarterTableColumn.Comment]: "Year-end volatility captured",
  },
];

export interface ReportQuarterTableProps {
  /** When provided, table is controlled by parent (e.g. for PDF export). */
  rows?: QuarterRow[];
  onRowsChange?: (rows: QuarterRow[]) => void;
}

const currencyWithDecimals = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const currencyWithoutDecimals = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const ReportQuarterTable = ({
  rows: controlledRows,
  onRowsChange,
}: ReportQuarterTableProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<QuarterRow | undefined>(
    undefined,
  );

  const [internalRows, setInternalRows] = useState<QuarterRow[]>(defaultQuarterRows);
  const rows = controlledRows ?? internalRows;
  const setRows = useCallback(
    (updater: (prev: QuarterRow[]) => QuarterRow[]) => {
      const next = updater(rows);
      if (onRowsChange) {
        onRowsChange(next);
      } else {
        setInternalRows(next);
      }
    },
    [rows, onRowsChange],
  );

  const handleRowClick = useCallback((params: { row: QuarterRow }) => {
    setSelectedRow(params.row);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedRow(undefined);
  }, []);

  const handleSubmit = useCallback(
    (data: QuarterRow) => {
      setRows((prevRows) =>
        prevRows.map((row) => (row.id === data.id ? data : row)),
      );
    },
    [setRows],
  );

  const gridColumns: GridColDef[] = useMemo(
    () => [
      {
        field: ReportQuarterTableColumn.Period,
        headerName: ReportQuarterTableColumn.Period,
        width: 150,
        flex: 1,
      },
      {
        field: ReportQuarterTableColumn.StockPrice,
        headerName: ReportQuarterTableColumn.StockPrice,
        width: 150,
        flex: 1,
        renderCell: (params) => {
          const value = params.value as number;
          return currencyWithDecimals.format(value);
        },
      },
      {
        field: ReportQuarterTableColumn.OptionIncome,
        headerName: ReportQuarterTableColumn.OptionIncome,
        width: 150,
        flex: 1,
        renderCell: (params) => {
          const value = params.value as number;
          return currencyWithoutDecimals.format(value);
        },
      },
      {
        field: ReportQuarterTableColumn.DividenIncome,
        headerName: ReportQuarterTableColumn.DividenIncome,
        width: 150,
        flex: 1,
        renderCell: (params) => {
          const value = params.value as number;
          return currencyWithoutDecimals.format(value);
        },
      },
      {
        field: ReportQuarterTableColumn.CumulativeIncome,
        headerName: ReportQuarterTableColumn.CumulativeIncome,
        width: 180,
        flex: 1,
        renderCell: (params) => {
          const value = params.value as number;
          return currencyWithoutDecimals.format(value);
        },
      },
      {
        field: ReportQuarterTableColumn.Comment,
        headerName: ReportQuarterTableColumn.Comment,
        width: 200,
        flex: 2,
        renderCell: (params) => {
          return <span style={{ fontStyle: "italic" }}>{params.value}</span>;
        },
      },
    ],
    [],
  );

  return (
    <Card sx={{ marginBottom: 2 }}>
      <DataGrid
        autoHeight
        rows={rows}
        columns={gridColumns}
        hideFooter
        disableColumnMenu
        disableRowSelectionOnClick
        disableColumnFilter
        onRowClick={handleRowClick}
        sx={reportQuarterTableStyles.gridContainer}
      />
      <QuarterTableEditModal
        isOpened={isModalOpen}
        onClose={handleCloseModal}
        quarterRow={selectedRow}
        onSubmit={handleSubmit}
      />
    </Card>
  );
};

export default ReportQuarterTable;
